import React from "react";
import {
	addTimeToDate,
	Column,
	Conditional,
	Else,
	filterMap,
	If,
	isAfter,
	Row,
	stringToLocalDate,
	toLocalDateString,
} from "@hex-insights/core";
import { Tile } from "../../../../../Components/Tile";
import { Timeline } from "../../../../../Components/Timeline";
import {
	HomeRoomSectionDetailQuery,
	TimelineFilterInput,
	timelineObjectStudentNoteTypesWithoutAttendance,
	TimelineObjectType,
	useCourseSectionListQuery,
} from "../../../../../Utilities";
import { StudentHomeRoomSectionEnrollmentUtilities, StudentUtilities } from "../../../../Utilities";
import * as ModelComponents from "../../../_General";
import * as CourseSectionComponents from "../../../CourseSection";
import * as SchoolAttendanceRecordComponents from "../../../SchoolAttendanceRecord";

export type BodyProps = {
	homeRoomSection: HomeRoomSectionDetailQuery["homeRoomSection"];
};

export function Body({ homeRoomSection }: BodyProps) {
	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column
				justify="spaced-start"
				style={{
					width: "25rem",
					flexShrink: 0,
					position: "sticky",
					top: "calc(var(--header---height) + 0.75rem)",
					height: "fit-content",
				}}
			>
				<AttendanceSection homeRoomSection={homeRoomSection} />
				<LessonPlansSection homeRoomSection={homeRoomSection} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<CourseSectionsSection homeRoomSection={homeRoomSection} />
				<TimelineSection homeRoomSection={homeRoomSection} />
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	homeRoomSection: HomeRoomSectionDetailQuery["homeRoomSection"];
};

function AttendanceSection({ homeRoomSection }: PageSectionProps) {
	const studentFilters = React.useMemo(
		() => StudentUtilities.getEnrolledInHomeRoomSectionOnDateFilterInput(homeRoomSection.id),
		[homeRoomSection.id],
	);

	return <SchoolAttendanceRecordComponents.SchoolAttendanceRecordReportTile studentFilters={studentFilters} />;
}

function LessonPlansSection(_: PageSectionProps) {
	return <ModelComponents.LessonPlansAggregrateReportTile />;
}

function CourseSectionsSection({ homeRoomSection }: PageSectionProps) {
	const { loading, data } = useCourseSectionListQuery({
		variables: {
			filters: {
				homeRoomSectionIDEQ: homeRoomSection.id,
			},
		},
	});
	const courseSections = React.useMemo(() => data?.courseSectionConnection.edges.map((e) => e.node), [data]);

	return (
		<CourseSectionComponents.CourseSectionsPaginatedGridTile
			isLoading={loading}
			courseSections={courseSections}
			columns={2}
			rows={3}
		/>
	);
}

function TimelineSection({ homeRoomSection }: PageSectionProps) {
	const { filter, students } = React.useMemo(() => {
		const hasEnded = isAfter(new Date(), stringToLocalDate(homeRoomSection.academicTerm.endDate, "day"), "day");

		const students = filterMap(
			homeRoomSection.studentHomeRoomSectionEnrollments,
			(e) => StudentHomeRoomSectionEnrollmentUtilities.isActive(e),
			(e) => e.student,
		);

		const filter: TimelineFilterInput = {
			objectTypeIn: [
				TimelineObjectType.AcademicTerm,
				TimelineObjectType.Announcement,
				TimelineObjectType.CalendarEvent,
				TimelineObjectType.Newsletter,
				TimelineObjectType.Post,
				TimelineObjectType.StudentNote,
				...timelineObjectStudentNoteTypesWithoutAttendance,
			],
			startTime: toLocalDateString(
				addTimeToDate(stringToLocalDate(homeRoomSection.academicTerm.startDate, "day"), [-1, "day"]),
			),
			endTime: hasEnded
				? toLocalDateString(addTimeToDate(stringToLocalDate(homeRoomSection.academicTerm.startDate, "day"), [1, "day"]))
				: undefined,
			homeRoomSectionID: homeRoomSection.id,
		};

		return { filter, students };
	}, [homeRoomSection]);

	return (
		<Tile style={{ flexGrow: 1 }}>
			<Tile.Body>
				<Conditional>
					<If condition={!students || students.length === 0}>
						<Column style={{ height: "100%" }}>
							<Timeline.Empty />
						</Column>
					</If>
					<Else>
						<Timeline filter={filter} pageSize={10} style={{ height: "100%" }} />
					</Else>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}
